/**
 * PlannerDataBadge – Visual indicator for data that came from the planner's estate plan.
 *
 * Used in the Planner Path to distinguish read-only planner data from editable settlement data.
 * Variants:
 *  - "pre-uploaded" – Documents uploaded during planning
 *  - "from-planning" – Data entered during planning (contacts, accounts, etc.)
 *  - "read-only" – Generic read-only indicator
 */

type BadgeVariant = 'pre-uploaded' | 'from-planning' | 'read-only';

interface PlannerDataBadgeProps {
  variant?: BadgeVariant;
  /** Optional count, e.g. "3 files from planning" */
  count?: number;
  label?: string;
  className?: string;
}

const VARIANT_CONFIG: Record<BadgeVariant, { icon: string; defaultLabel: string; bg: string; text: string }> = {
  'pre-uploaded': {
    icon: '📎',
    defaultLabel: 'from planning',
    bg: 'bg-green-100 border-green-300',
    text: 'text-green-900',
  },
  'from-planning': {
    icon: '📋',
    defaultLabel: 'from planning',
    bg: 'bg-green-100 border-green-300',
    text: 'text-green-900',
  },
  'read-only': {
    icon: '🔒',
    defaultLabel: 'Read-only',
    bg: 'bg-grey-200 border-grey-300',
    text: 'text-grey-600',
  },
};

export default function PlannerDataBadge({
  variant = 'from-planning',
  count,
  label,
  className = '',
}: PlannerDataBadgeProps) {
  const config = VARIANT_CONFIG[variant];
  const displayLabel = label || (count ? `${count} ${count === 1 ? 'file' : 'files'} ${config.defaultLabel}` : config.defaultLabel);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${config.bg} ${config.text} ${className}`}
    >
      <span className="text-sm leading-none">{config.icon}</span>
      {displayLabel}
    </span>
  );
}
